import type { Pt2 } from './mercator'

const cross = ([ox, oy]: Pt2, [ax, ay]: Pt2, [bx, by]: Pt2) =>
  (ax - ox) * (by - oy) - (ay - oy) * (bx - ox)

/**
 * Andrew's monotone chain, the same algorithm and the same conventions as the
 * app's `calculateConvexHull`: counter-clockwise output, collinear boundary
 * points dropped, duplicates removed, and an input of one or zero points
 * returned unchanged. The wash fits its ellipse to the hull rather than to the
 * raw ring, because the fit is iterative and a parcel outline can carry tens of
 * thousands of vertices.
 */
export function convexHull(points: readonly Pt2[]): Pt2[] {
  if (points.length <= 1) return points.map(([x, y]) => [x, y])
  const sorted = [...points].sort(([ax, ay], [bx, by]) => (ax === bx ? ay - by : ax - bx))
  const uniq: Pt2[] = []
  for (const p of sorted) {
    const last = uniq[uniq.length - 1]
    if (!last || last[0] !== p[0] || last[1] !== p[1]) uniq.push(p)
  }
  if (uniq.length <= 1) return uniq

  const half = (pts: readonly Pt2[]): Pt2[] => {
    const out: Pt2[] = []
    for (const p of pts) {
      while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], p) <= 0) out.pop()
      out.push(p)
    }
    out.pop()
    return out
  }
  return [...half(uniq), ...half([...uniq].reverse())]
}
